/**
 * varuna への通信に、ログイン中の会員の Firebase ID トークンを付ける。
 *
 * varuna の会員 API は、送られてきた uuid が本人のものかを
 * `Authorization: Bearer <ID トークン>` で確かめる（services/member_token_auth.py）。
 * 呼び出しごとに付けると漏れが出るため、グローバルの axios に一度だけ組み込む。
 *
 * - 送り先が varuna（VUE_APP_ENDPOINT_PREFIX で始まる URL）のときだけ付ける
 * - 呼び出し側が Authorization を指定していればそのまま使う（退会のように、
 *   Firebase のアカウントを消す前に取ったトークンを渡す場合など）
 * - 未ログインなら何もしない
 */
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { getFirebaseAuth } from '@/infra/firebase/auth'

const ENDPOINT_PREFIX = process.env.VUE_APP_ENDPOINT_PREFIX || ''
/** ID トークン取得の上限。期限が近いと Firebase へ取り直しに行くため、応答が無いと通信が止まる */
const ID_TOKEN_TIMEOUT_MS = 10000

/** 時間切れなら null（トークンなしで送る） */
function idTokenOrNull(user: { getIdToken: () => Promise<string> }) {
  return Promise.race<string | null>([
    user.getIdToken(),
    new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), ID_TOKEN_TIMEOUT_MS)
    )
  ])
}

function isVarunaUrl(url: string | undefined, prefix = ENDPOINT_PREFIX) {
  return !!url && !!prefix && url.startsWith(prefix)
}

function hasAuthorization(config: AxiosRequestConfig) {
  const headers = (config.headers || {}) as Record<string, unknown>
  return !!(headers.Authorization || headers.authorization)
}

async function attachVarunaIdToken(
  config: AxiosRequestConfig
): Promise<AxiosRequestConfig> {
  if (!isVarunaUrl(config.url) || hasAuthorization(config)) return config
  const user = getFirebaseAuth().currentUser
  if (!user) return config
  try {
    const idToken = await idTokenOrNull(user)
    if (!idToken) return config
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${idToken}`
    }
  } catch (e) {
    // トークンが取れなくても通信は止めない（サーバ側が記録モードの間は従来どおり動く）
    console.warn('ID トークンの取得に失敗:', e)
  }
  return config
}

export function installVarunaAuthHeader(instance: AxiosInstance) {
  instance.interceptors.request.use(attachVarunaIdToken)
}
